import React, { createContext, useContext, useEffect, ReactNode, useMemo, useCallback, useRef } from "react";
import { ttsService, TTSPlaybackState } from "../services/TTSService";
import { useToast } from "./ToastContext";

interface AudioContextType {
  stopAudio: () => void;
  speak: (text: string, messageId: string, voiceName: string) => Promise<void>;
  pauseAudio: () => void;
  resumeAudio: () => void;
  isPlaying: boolean;
  currentUrl: string | null;
  currentAudioMessageId: string | null;
  isMessageConverting: boolean;
  setIsMessageConverting: (isMessageConverting: boolean) => void;
  currentTime: number;
  duration: number;
  isPaused: boolean;
  hasFinished: boolean;
}

const AudioContext = createContext<AudioContextType | undefined>(undefined);

export const useAudio = () => {
  const context = useContext(AudioContext);
  if (!context) {
    throw new Error("useAudio must be used within an AudioProvider");
  }
  return context;
};

interface AudioProviderProps {
  children: ReactNode;
}

export const AudioProvider: React.FC<AudioProviderProps> = ({ children }) => {
  const [playbackState, setPlaybackState] = React.useState<TTSPlaybackState>({
    isPlaying: false,
    isConverting: false,
    currentMessageId: null,
    currentTime: 0,
    duration: 0,
    isPaused: false,
    hasFinished: false
  });

  // `useToast` returns a new function every render; read the latest through a ref.
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;

  // Setup TTS service state updates
  useEffect(() => {
    const handleStateUpdate = (state: TTSPlaybackState) => {
      setPlaybackState(state);
    };

    ttsService.onStateUpdate(handleStateUpdate);
    ttsService.onError((message) => toastRef.current(message, "error"));

    // Cleanup on unmount
    return () => {
      ttsService.cleanup();
    };
  }, []);

  // Must be called straight from a click handler: the service unlocks audio playback
  // synchronously before its first await. Generation and playback errors surface as toasts.
  const speak = useCallback((text: string, messageId: string, voiceName: string): Promise<void> => {
    return ttsService.speak(text, {
      voice: voiceName,
      messageId: messageId
    });
  }, []);

  // Memoized stop function
  const stopAudio = useCallback(() => {
    ttsService.stop();
  }, []);

  // Memoized pause function
  const pauseAudio = useCallback(() => {
    ttsService.pause();
  }, []);

  // Memoized resume function
  const resumeAudio = useCallback(() => {
    ttsService.resume();
  }, []);

  // Legacy setIsMessageConverting for backward compatibility
  const setIsMessageConverting = useCallback((isConverting: boolean) => {
    // This is now handled internally by TTSService
    // Keeping for backward compatibility but it's essentially a no-op
    console.warn('setIsMessageConverting is deprecated. State is now managed automatically.');
  }, []);

  // Memoized context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    stopAudio,
    speak,
    pauseAudio,
    resumeAudio,
    isPlaying: playbackState.isPlaying,
    currentUrl: null, // Legacy field - not used with new service
    currentAudioMessageId: playbackState.currentMessageId,
    isMessageConverting: playbackState.isConverting,
    setIsMessageConverting,
    currentTime: playbackState.currentTime,
    duration: playbackState.duration,
    isPaused: playbackState.isPaused,
    hasFinished: playbackState.hasFinished
  }), [
    stopAudio,
    speak,
    pauseAudio,
    resumeAudio,
    playbackState.isPlaying,
    playbackState.currentMessageId,
    playbackState.isConverting,
    setIsMessageConverting,
    playbackState.currentTime,
    playbackState.duration,
    playbackState.isPaused,
    playbackState.hasFinished
  ]);

  return (
    <AudioContext.Provider value={contextValue}>
      {children}
    </AudioContext.Provider>
  );
};
